import { useState } from 'react'
import { supabase } from '../supabase'

function OrgSetup({ session, onComplete }) {
  const [mode, setMode] = useState('choose')
  const [orgName, setOrgName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleCreateOrg = async () => {
    if (!orgName.trim()) return
    setSaving(true)
    setError(null)

    try {
      // Generate slug from org name
      const slug = orgName.trim().toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')

      // Check slug not taken
      const { data: existing } = await supabase
        .from('organisations')
        .select('id')
        .eq('slug', slug)
        .single()

      if (existing) {
        setError('An organisation with that name already exists. Please choose a different name.')
        setSaving(false)
        return
      }

      // Create the organisation
      const { data: org, error: orgError } = await supabase
        .from('organisations')
        .insert([{
          name: orgName.trim(),
          slug,
          licence_tier: 'essentials',
          licence_active: true,
          primary_colour: '2D6A4F',
          secondary_colour: 'C9A84C',
          powered_by_cadence: true,
          created_by: session.user.id
        }])
        .select()
        .single()

      if (orgError) throw orgError

      // Update profile with org and admin role
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          organisation_id: org.id,
          role: 'admin'
        })
        .eq('id', session.user.id)

      if (profileError) throw profileError

      onComplete()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleJoinOrg = async () => {
    if (!inviteCode.trim()) return
    setSaving(true)
    setError(null)

    try {
      // Look up invitation by token
      const { data: invite, error: inviteError } = await supabase
        .from('invitations')
        .select('*, organisations(id, name)')
        .eq('token', inviteCode.trim())
        .eq('email', session.user.email)
        .eq('accepted', false)
        .single()

      if (inviteError || !invite) {
        setError('Invitation not found or already used. Please check your code or contact your administrator.')
        setSaving(false)
        return
      }

      // Check not expired
      if (new Date(invite.expires_at) < new Date()) {
        setError('This invitation has expired. Please ask your administrator to send a new one.')
        setSaving(false)
        return
      }

      // Link user to organisation
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          organisation_id: invite.organisation_id,
          role: invite.role
        })
        .eq('id', session.user.id)

      if (profileError) throw profileError

      // Mark invitation as accepted
      await supabase
        .from('invitations')
        .update({ accepted: true })
        .eq('id', invite.id)

      onComplete()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--green-dark)',
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
      backgroundSize: '28px 28px'
    }}>
      <div style={{
        background: 'var(--white)', padding: '48px',
        width: '100%', maxWidth: '520px'
      }}>
        <div style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '1.75rem', fontWeight: 600,
          color: 'var(--green-dark)', marginBottom: '8px'
        }}>
          Welcome to Cadence
        </div>
        <p style={{ fontSize: '0.9375rem', color: 'var(--slate)', marginBottom: '32px' }}>
          To get started, create a new organisation or join an existing one with an invitation code.
        </p>

        {error && <div className="error-msg">{error}</div>}

        {mode === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              className="btn btn--primary btn--full"
              onClick={() => setMode('create')}
            >
              Create a New Organisation
            </button>
            <button
              className="btn btn--secondary btn--full"
              onClick={() => setMode('join')}
            >
              Join an Existing Organisation
            </button>
          </div>
        )}

        {mode === 'create' && (
          <div>
            <div className="form-group">
              <label>Organisation Name *</label>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="e.g. Acme Defence Ltd"
                autoFocus
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '4px' }}>
                This will be your organisation's display name in Cadence.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn--primary"
                onClick={handleCreateOrg}
                disabled={saving || !orgName.trim()}
              >
                {saving ? 'Creating...' : 'Create Organisation'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => { setMode('choose'); setError(null) }}
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        {mode === 'join' && (
          <div>
            <div className="form-group">
              <label>Invitation Code *</label>
              <input
                type="text"
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value)}
                placeholder="Paste your invitation code here"
                autoFocus
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '4px' }}>
                Your administrator will have sent you an invitation code by email.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn--primary"
                onClick={handleJoinOrg}
                disabled={saving || !inviteCode.trim()}
              >
                {saving ? 'Joining...' : 'Join Organisation'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => { setMode('choose'); setError(null) }}
              >
                ← Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrgSetup